import React, { useMemo, useState } from "react";
import keycloak from "../APIs/keycloak";
import { useCurrentUser } from "../APIs/UserContext";
import Header from "../modals/Header";
import ProviderVerificationDocumentModal from "../modals/ProviderVerificationDocumentModal";

const DOC_LABELS = {
  GOVERNMENT_ID: "Government ID",
  MEDICAL_LICENSE: "Medical License",
  DEA_LICENSE: "DEA License",
  BOARD_CERTIFICATION: "Board Certification",
  NURSING_LICENSE: "Nursing License",
  PHYSICIAN_ASSISTANT_LICENSE: "Physician Assistant License",
  THERAPIST_LICENSE: "Therapist License",
  EMPLOYMENT_VERIFICATION: "Employment Verification",
  FACILITY_BADGE: "Facility Badge",
  BUSINESS_CARD: "Business Card",
  MALPRACTICE_INSURANCE: "Malpractice Insurance",
  OTHER: "Other",
};

export default function ProviderVerificationPending() {
  const { currentUser, refreshUser } = useCurrentUser();
  const [uploadOpen, setUploadOpen] = useState(false);

  const status = currentUser?.providerVerificationStatus || "PENDING_REVIEW";
  const requestedDocs = useMemo(() => {
    const raw = currentUser?.providerVerificationRequestedDocumentTypes || "";
    return raw
      .split(",")
      .map((value) => value.trim())
      .filter(Boolean);
  }, [currentUser]);

  const handleSignOut = () => {
    keycloak.logout({
      redirectUri: window.location.origin + "/ProviderSearchPage",
    });
  };

  const title =
    status === "DOCUMENTS_REQUESTED"
      ? "Additional documents requested"
      : status === "REJECTED"
        ? "Your verification request was not approved"
        : "Your account is pending verification";

  const message =
    status === "DOCUMENTS_REQUESTED"
      ? "An admin needs more information before your provider account can be verified."
      : status === "REJECTED"
        ? "Your provider verification request was rejected. Review the reason below before contacting MedMaze support."
        : "Thank you. Your provider account has been submitted for review and will be approved after your document is verified.";

  return (
    <div className="min-h-screen bg-gray-50">
      <Header />

      <main className="px-4 py-16">
        <section className="mx-auto max-w-xl rounded-lg border border-gray-200 bg-white p-8 text-center shadow-sm">
          <div className="mx-auto mb-5 flex h-14 w-14 items-center justify-center rounded-full bg-teal-50 text-2xl text-teal-700">
            ✓
          </div>

          <h1 className="text-2xl font-bold text-gray-900">{title}</h1>

          <p className="mt-3 text-sm leading-6 text-gray-600">{message}</p>

          <div className="mt-6 rounded border border-amber-200 bg-amber-50 px-4 py-3 text-sm font-medium text-amber-800">
            Status:{" "}
            {status === "DOCUMENTS_REQUESTED"
              ? "Documents Requested"
              : status === "REJECTED"
                ? "Rejected"
                : "Pending Verification"}
          </div>

          {status === "DOCUMENTS_REQUESTED" && (
            <div className="mt-5 rounded border border-gray-200 bg-gray-50 p-4 text-left text-sm">
              <div className="font-semibold text-gray-900">
                Please upload one of the following:
              </div>
              <ul className="mt-2 list-disc pl-5 text-gray-700">
                {requestedDocs.map((type) => (
                  <li key={type}>{DOC_LABELS[type] || type}</li>
                ))}
              </ul>
              {currentUser?.providerVerificationRequestMessage && (
                <p className="mt-3 text-gray-700">
                  {currentUser.providerVerificationRequestMessage}
                </p>
              )}
              <button
                type="button"
                onClick={() => setUploadOpen(true)}
                className="mt-4 rounded bg-teal-600 px-4 py-2 text-sm font-semibold text-white hover:bg-teal-700"
              >
                Upload document
              </button>
            </div>
          )}

          {status === "REJECTED" &&
            currentUser?.providerVerificationRejectionReason && (
              <div className="mt-5 rounded border border-red-200 bg-red-50 p-4 text-left text-sm text-red-800">
                Reason: {currentUser.providerVerificationRejectionReason}
              </div>
            )}

          <button
            type="button"
            onClick={handleSignOut}
            className="mt-6 rounded border border-gray-300 px-4 py-2 text-sm font-medium text-gray-700 hover:bg-gray-50"
          >
            Sign out
          </button>
        </section>
      </main>

      <ProviderVerificationDocumentModal
        open={uploadOpen}
        npi={currentUser?.providerNpi}
        onClose={() => setUploadOpen(false)}
        onBack={() => setUploadOpen(false)}
        onUploaded={async () => {
          setUploadOpen(false);
          await refreshUser();
        }}
      />
    </div>
  );
}
